// AMP owns the delivery rules and attribute writes. This adapter only verifies
// saved values and refreshes its official embed after merchandise changes.
export const normalizeDeliveryDate = (value) => String(value ?? '').match(/\d{4}[-/]\d{2}[-/]\d{2}/)?.[0].replaceAll('/', '-') || '';

export function deliveryMatches(snapshot, attributes = {}) {
  if (!snapshot) return false;
  return attributes['配送日の指定'] === (snapshot.date ? 'あり' : 'なし') &&
    normalizeDeliveryDate(attributes['配送希望日']) === normalizeDeliveryDate(snapshot.date) &&
    attributes['配送時間帯'] === snapshot.time;
}

export const cartRevision = (cart) => (cart.items || []).map((item) => `${item.key}:${item.quantity};`).join('');

// Share an edit epoch and its delivery choice, never the raw cart token.
// Restoration keeps its epoch; only an explicit user choice publishes a new one.
export function createDeliveryChoiceSync({ readCart, storage = () => localStorage,
  hash = async (value) => [...new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)))].map((b) => b.toString(16).padStart(2, '0')).join('') }) {
  let ready;
  let key;
  let usable = false;
  const signature = (selection) => JSON.stringify([selection?.prefecture || '', normalizeDeliveryDate(selection?.date), selection?.time || '指定なし']);
  const marker = () => {
    try {
      const value = JSON.parse(storage().getItem(key) || 'null');
      if (value && (typeof value.id !== 'string' || !Number.isFinite(value.at))) throw new Error('Invalid edit epoch');
      return value;
    } catch { usable = false; return null; }
  };
  const prepare = () => {
    if (usable) return Promise.resolve(true);
    if (ready) return ready;
    const pending = (async () => {
      try {
        const cart = await readCart();
        if (!cart.token) return false;
        key = `jp-delivery-edit:${await hash(String(cart.token))}`;
        // Readable but unwritable storage cannot coordinate explicit edits.
        storage().setItem(`${key}:probe`, '1');
        storage().removeItem(`${key}:probe`);
        usable = true;
        marker();
      } catch { usable = false; }
      return usable;
    })();
    ready = pending;
    void pending.finally(() => { if (ready === pending) ready = undefined; });
    return pending;
  };
  return {
    prepare,
    get key() { return key; },
    get usable() { return usable; },
    epochFor(selection) {
      const latest = usable ? marker() : null;
      // A marker can precede AMP's save. Never label stale visible controls
      // with the new epoch just because this tab has no recorded intent yet.
      return latest?.choice === signature(selection) ? latest.id : undefined;
    },
    changed(selection) {
      if (!selection || !usable) return false;
      const latest = marker();
      return Boolean(latest && latest.id !== selection.choiceId);
    },
    async publish(selection, isCurrent = () => true) {
      // Once scoped, publish synchronously in the explicit change handler.
      // Wall-clock timestamps cannot order edits (ties and clock rollback).
      const pendingIdentity = !usable;
      if (pendingIdentity && !await prepare()) return;
      if (!isCurrent()) return;
      const latest = marker();
      if (!usable) return;
      // Identity finished late and another edit exists: its ordering is
      // unknowable. Invalidate BOTH epochs rather than replay either choice.
      const id = pendingIdentity && latest ? `conflict:${selection.choiceId}` : selection.choiceId;
      const choice = pendingIdentity && latest ? null : signature(selection);
      try { storage().setItem(key, JSON.stringify({ id, at: selection.choiceAt, choice })); }
      catch { usable = false; }
    },
  };
}

export function cartTermsSatisfied(form, terms) {
  const required = form?.dataset?.jpTermsRequired === 'true' || terms?.required;
  return !required || Boolean(terms?.required && terms.checked && terms.form === form);
}

export function selectionForStorage(selection, changedPrefecture) {
  if (changedPrefecture === undefined) return selection || undefined; // Incomplete is not a request to forget.
  if (!changedPrefecture || changedPrefecture === 'JP-99') return null;
  return { prefecture: changedPrefecture, date: '', time: '指定なし' };
}

export function readDeliverySnapshot(app) {
  if (!app || [...app.querySelectorAll('.error-message')].some((node) => node.textContent.trim())) return null;
  if (app.querySelector('#undeliverable-statement')) return { date: '', time: '指定なし' };
  if (!app.querySelector('#delivery-datetime--isRender')) return null;
  const date = app.querySelector('[name="delivery_date"]');
  const time = app.querySelector('[name="delivery_time"]');
  const prefecture = app.querySelector('[name="prefecture"]');
  // AMP can mount a time-only shell in a closed PDP drawer. That shell is
  // not a choice to clear the date already saved in the cart.
  if (prefecture && (!prefecture.value || prefecture.value === 'JP-99' || !date)) return null;
  if (!date && !time) return null;
  return { date: normalizeDeliveryDate(date?.value), time: time?.value || '指定なし' };
}

export async function waitForDeliverySave({ readSnapshot, readCart, sleep, expectedRevision, waitUntilReady = false, now = Date.now, timeout = 8000 }) {
  const deadline = now() + timeout;
  let previous = '';
  while (now() < deadline) {
    const snapshot = readSnapshot();
    if (!snapshot) {
      if (waitUntilReady) { previous = ''; await sleep(100); continue; }
      throw new Error('配送指定を読み込めませんでした。カートページで確認してください。');
    }
    const cart = await readCart();
    if (expectedRevision !== undefined && cartRevision(cart) !== expectedRevision) {
      throw new Error('別の画面でカートが変更されました。カートページを開き直して配送条件を確認してください。');
    }
    const current = readSnapshot();
    const signature = JSON.stringify(current);
    if (signature === JSON.stringify(snapshot) && deliveryMatches(current, cart.attributes)) {
      if (previous === signature) return cart;
      previous = signature;
    } else previous = '';
    await sleep(200);
  }
  throw new Error('配送指定の保存を確認できませんでした。時間をおいて再度お試しいただくか、カートページで確認してください。');
}

export async function prepareDeliveryMutation(options) {
  // An untouched, incomplete embed has no new delivery choice to save. If
  // a change was dispatched in this page, wait for the app to finish instead.
  if (!options.edited && !options.readSnapshot()) return;
  return waitForDeliverySave({ ...options, waitUntilReady: true });
}

export async function waitForQuantityReservations({ isQueued, sleep, now = Date.now, timeout = 8000 }) {
  const deadline = now() + timeout;
  while (isQueued()) {
    if (now() >= deadline) throw new Error('数量の更新を確認できませんでした。カートページで確認してください。');
    await sleep(50);
  }
}

export async function readSettledCart({ readCart, isPending, getVersion, sleep, now = Date.now, timeout = 8000 }) {
  const deadline = now() + timeout;
  while (now() < deadline) {
    if (isPending()) { await sleep(100); continue; }
    const version = getVersion();
    const cart = await readCart();
    if (!isPending() && version === getVersion()) return cart;
  }
  throw new Error('カートの更新を確認できませんでした。カートページで確認してください。');
}

export function createCartMutationQueue() {
  let tail = Promise.resolve();
  return (prepare) => {
    let settle;
    const done = new Promise((resolve) => { settle = resolve; });
    const ready = tail.then(prepare);
    tail = ready.catch(() => {}).then(() => done);
    return { ready, settle };
  };
}

export function queueMerchandiseMutation({ kind, queue, isQuantityQueued, sleep, prepare }) {
  let release = () => {};
  const ready = (async () => {
    // Reserve a queue slot only AFTER earlier quantity clicks have enqueued
    // their absolute quantity request. Otherwise an add can be overwritten.
    if (kind !== 'quantity') await waitForQuantityReservations({ isQueued: isQuantityQueued, sleep });
    const entry = queue(prepare);
    release = entry.settle;
    await entry.ready;
  })();
  return { ready, settle: () => release() };
}

// Use only choices that the freshly initialized official app offers. Never
// manufacture an option or write AMP attributes independently of its UI.
export async function restoreDeliveryChoice({ selection, readControls, choose, sleep, isCurrent = () => true, now = Date.now, timeout = 12000 }) {
  const deadline = now() + timeout;
  let previous = '';
  while (now() < deadline && isCurrent()) {
    const controls = readControls();
    if (controls?.ineligible) return { ineligible: true };
    if (!controls?.ready) { await sleep(100); continue; }
    const region = controls.prefecture;
    if (region) {
      if (!selection.prefecture || !region.options.includes(selection.prefecture)) return { invalid: true };
      if (region.value !== selection.prefecture) { choose('prefecture', selection.prefecture); await sleep(200); continue; }
    }
    if (controls.pending || (selection.date && !controls.date)) { await sleep(100); continue; }
    if (selection.time && selection.time !== '指定なし' && !controls.time) { await sleep(100); continue; }
    const wantedDate = selection.date && controls.date?.options.find((value) => normalizeDeliveryDate(value) === selection.date);
    if (selection.date && !wantedDate) return { invalid: true };
    if (wantedDate && controls.date.value !== wantedDate) { choose('delivery_date', wantedDate); await sleep(200); continue; }
    if (!selection.date && normalizeDeliveryDate(controls.date?.value)) {
      const unspecified = controls.date.options.find((value) => !normalizeDeliveryDate(value));
      if (unspecified === undefined) return { invalid: true };
      choose('delivery_date', unspecified); await sleep(200); continue;
    }
    const wantedTime = selection.time || '指定なし';
    if (controls.time && !controls.time.options.includes(wantedTime)) return { invalid: true };
    if (controls.time && controls.time.value !== wantedTime) { choose('delivery_time', wantedTime); await sleep(200); continue; }
    const signature = JSON.stringify(controls);
    if (signature === previous) return { restored: true };
    previous = signature;
    await sleep(200);
  }
  if (!isCurrent()) return { cancelled: true };
  throw new Error('配送指定を復元できませんでした。配送日時を選び直してください。');
}

export async function restoreAndVerifyDeliveryChoice(options) {
  const { selection, readSnapshot, readCart, sleep, isCurrent = () => true, now = Date.now, timeout = 12000 } = options;
  const deadline = now() + timeout;
  const expected = { date: normalizeDeliveryDate(selection.date), time: selection.time || '指定なし' };
  const matches = (snapshot) => {
    const region = options.readControls()?.prefecture;
    return snapshot && normalizeDeliveryDate(snapshot.date) === expected.date && snapshot.time === expected.time && (!region || region.value === selection.prefecture);
  };
  while (now() < deadline && isCurrent()) {
    const result = await restoreDeliveryChoice({ ...options, timeout: Math.max(1, deadline - now()) });
    if (!isCurrent()) return { cancelled: true };
    if (!result.restored) return result;
    let confirmed = false;
    while (now() < deadline && isCurrent()) {
      if (!matches(readSnapshot())) break;
      const cart = await readCart(Math.min(4000, Math.max(1, deadline - now())));
      if (!isCurrent()) return { cancelled: true };
      if (now() >= deadline) break;
      // AMP can finish an older initialization after the controls appeared
      // stable. A matching reset in DOM and cart is not the requested choice.
      if (!matches(readSnapshot())) break;
      if (deliveryMatches(expected, cart.attributes)) {
        if (confirmed) return { restored: true };
        confirmed = true;
      } else confirmed = false;
      await sleep(200);
    }
  }
  if (!isCurrent()) return { cancelled: true };
  throw new Error('配送指定の保存を確認できませんでした。配送日時を選び直してください。');
}

if (typeof window !== 'undefined') installDeliveryCheckout();

function installDeliveryCheckout() {
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const appSelector = '#cart-delivery-datetime_embedded, #cart-delivery-datetime_section_os2';
  let approvedButton = null;
  let verifying = false;
  let refreshing = false;
  let merchandisePending = false;
  let activeUpdates = 0;
  let quantityQueued = false;
  const quantityOwners = new Set();
  let deliveryStale = false;
  let cartVersion = 0;
  let reloadQueued = false;
  let restoring = false;
  let applyingChoice = false;
  let deliveryEdited = false;
  let restorationVersion = 0;
  let reconciliationPaused = false;
  let reselectionRequired = false;
  let otherTabChanged = false;
  let confirmedChoice = '';
  let savedChoiceCheck;
  let lastSavedChoiceCheck = 0;
  const selectionKey = 'jp-cart-delivery-selection';
  const choiceSync = createDeliveryChoiceSync({ readCart });
  const mutationQueue = createCartMutationQueue();
  const releases = new WeakMap();
  const baseline = document.querySelector('[data-jp-cart-revision]')?.dataset.jpCartRevision ?? '';
  const debug = new URLSearchParams(location.search).get('jp_cart_debug') === '1';
  function trace(kind, detail = {}) {
    if (!debug) return;
    try {
      const history = JSON.parse(sessionStorage.getItem('jp-cart-debug') || '[]');
      history.push({ kind, at: Date.now(), version: cartVersion, pending: merchandisePending, active: activeUpdates, queued: quantityQueued, ...detail });
      const value = JSON.stringify(history.slice(-40));
      sessionStorage.setItem('jp-cart-debug', value);
      document.documentElement.dataset.jpCartTrace = value;
    } catch { /* Debugging must not affect a cart operation. */ }
  }
  trace('boot');
  window.addEventListener('beforeunload', () => trace('unload'));
  window.addEventListener('click', (event) => {
    const quantity = event.target?.closest?.('cart-quantity-selector-component');
    if (quantity?.closest('cart-items-component')) trace('quantity-click', { label: event.target?.closest('button')?.getAttribute('aria-label'), value: quantity.querySelector('input')?.value });
  }, { capture: true });

  function deliveryApp() {
    return [...document.querySelectorAll(appSelector)].find((app) => app.getClientRects().length) || document.querySelector(appSelector);
  }

  function readSnapshot() {
    return readDeliverySnapshot(deliveryApp());
  }

  function readSelection() {
    const snapshot = readSnapshot();
    if (!snapshot) return null;
    const app = deliveryApp();
    return { ...snapshot, prefecture: app?.querySelector('[name="prefecture"]')?.value || '' };
  }

  function rememberSelection(selection, revision = baseline) {
    try {
      if (selection && selection.prefecture !== 'JP-99') sessionStorage.setItem(selectionKey, JSON.stringify({ ...selection, revision, at: Date.now() }));
      else sessionStorage.removeItem(selectionKey);
    } catch { /* Storage being unavailable must not bypass checkout validation. */ }
  }

  function rememberCurrentChoice() {
    // Explicit change events own the recorded intent. A later app render must
    // not replace it with an unsolicited reset just before a quantity update.
    if (!restoring && !requiresReselection() && !storedSelection() && deliveryApp()?.querySelector('[name="prefecture"], [name="delivery_date"], [name="delivery_time"]')) {
      rememberReadableChoice();
    }
  }

  function rememberReadableChoice(changedPrefecture) {
    const selection = selectionForStorage(readSelection(), changedPrefecture);
    if (selection !== undefined) {
      const previous = storedSelection(true);
      rememberSelection(selection && { ...selection, choiceId: previous?.choiceId ?? choiceSync.epochFor(selection), choiceAt: previous?.choiceAt });
    }
  }

  function publishExplicitChoice() {
    const selection = storedSelection(true);
    if (!selection) return;
    const choice = { ...selection, choiceId: `${Date.now()}:${Math.random().toString(36).slice(2)}`, choiceAt: Date.now() };
    rememberSelection(choice);
    void choiceSync.publish(choice, () => storedSelection(true)?.choiceId === choice.choiceId).then(() => {
      if (choiceSync.changed(storedSelection(true))) reportOtherTabChange();
    });
  }

  function reportOtherTabChange() {
    otherTabChanged = true;
    restorationVersion++;
    cartVersion++;
    restoring = false;
    reconciliationPaused = true;
    rememberSelection(null);
    status('別のタブで配送指定が変更されました。ページを再読み込みして最新の指定を確認するか、配送日時を選び直してください。');
  }

  function checkOtherTab(selection = storedSelection(true)) {
    if (choiceSync.changed(selection)) reportOtherTabChange();
    return otherTabChanged;
  }

  window.addEventListener('storage', (event) => {
    if (event.key === choiceSync.key) checkOtherTab();
  });

  function storedSelection(keepInvalid = false) {
    try {
      const selection = JSON.parse(sessionStorage.getItem(selectionKey) || 'null');
      return selection && selection.revision === baseline && Number.isFinite(selection.at)
        && ((keepInvalid && selection.requiresReselection === true) || Date.now() - selection.at <= 1800000) ? selection : null;
    } catch { return null; }
  }

  function requiresReselection() {
    if (reselectionRequired) return true;
    try {
      const selection = JSON.parse(sessionStorage.getItem(selectionKey) || 'null');
      // The preference expires after 30 minutes, but expiration is not consent
      // to discard an invalid choice for this same merchandise revision.
      return selection?.revision === baseline && selection.requiresReselection === true;
    } catch { return false; }
  }

  const choiceCheckpoint = (selection) => JSON.stringify([cartVersion, selection.choiceId, selection.prefecture, selection.date, selection.time]);

  // A request already sent by AMP in another tab can finish after our save.
  // Observe persisted attributes too: matching controls/marker alone cannot
  // prove the shared cart still contains this tab's confirmed choice.
  async function checkSavedChoice(force = false) {
    while (savedChoiceCheck) {
      if (!force) return;
      await savedChoiceCheck;
    }
    const selection = storedSelection();
    if (!selection?.choiceId || !choiceSync.usable) return;
    const checkpoint = choiceCheckpoint(selection);
    const current = () => !document.hidden && !restoring && !refreshing && !merchandisePending
      && (!verifying || force) && !reconciliationPaused && !requiresReselection()
      && storedSelection()?.choiceId === selection.choiceId && choiceCheckpoint(selection) === checkpoint
      && JSON.stringify(readSelection()) === JSON.stringify({ date: selection.date, time: selection.time, prefecture: selection.prefecture })
      && !checkOtherTab(selection);
    if (!current()) return;
    const now = Date.now();
    // Keep the shared cart read bounded even with several visible cart tabs.
    // Focus/visibility and checkout bypass this background-read throttle.
    if (!force && now >= lastSavedChoiceCheck && now - lastSavedChoiceCheck < 10000) return;
    lastSavedChoiceCheck = now;
    const pending = (async () => {
      try {
        const cart = await readCart();
        if (!current()) return;
        if (cartRevision(cart) !== baseline) { confirmedChoice = ''; return; }
        if (deliveryMatches(selection, cart.attributes)) { confirmedChoice = checkpoint; return; }
        // Do not interpret the user's own pending save as a late overwrite.
        if (confirmedChoice !== checkpoint) return;
        await sleep(200);
        const confirmation = await readCart();
        if (!current() || cartRevision(confirmation) !== baseline || deliveryMatches(selection, confirmation.attributes)) return;
        reconciliationPaused = true;
        reselectionRequired = true;
        restorationVersion++;
        cartVersion++;
        rememberSelection({ ...selection, requiresReselection: true });
        requestReselection();
      } catch { /* Checkout still requires its own fresh, successful readback. */ }
    })();
    savedChoiceCheck = pending;
    try { await pending; }
    finally { if (savedChoiceCheck === pending) savedChoiceCheck = undefined; }
  }

  function requestReselection() {
    status('配送条件が変わりました。配送日時を選び直してください。');
    const node = document.querySelector('[data-jp-delivery-status]');
    if (!node) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button-secondary';
    button.textContent = '配送日時を指定しない';
    button.dataset.jpDeliveryUnspecified = '';
    button.addEventListener('click', (event) => {
      if (!event.isTrusted || merchandisePending || refreshing || restoring) return;
      const app = deliveryApp();
      if (!readSnapshot()) { requestReselection(); return; }
      const controls = ['delivery_date', 'delivery_time'].map((name) => app?.querySelector(`select[name="${name}"]`));
      const values = controls.map((control, index) => control && [...control.options].find((option) =>
        !option.disabled && (index === 0 ? !normalizeDeliveryDate(option.value) : option.value === '指定なし'))?.value);
      if (!app?.querySelector('#undeliverable-statement') && controls.some((control, index) => !control || control.disabled || values[index] === undefined)) return;
      applyingChoice = true;
      try {
        controls.forEach((control, index) => {
          if (!app.querySelector('#undeliverable-statement') && control && !control.disabled && values[index] !== undefined) {
            control.value = values[index];
            control.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      } finally { applyingChoice = false; }
      const selection = readSelection();
      if (!selection || selection.date || selection.time !== '指定なし') { requestReselection(); return; }
      reselectionRequired = false;
      restorationVersion++;
      cartVersion++;
      reconciliationPaused = false;
      otherTabChanged = false;
      rememberSelection(selection);
      publishExplicitChoice();
      status('');
      // Acknowledging the choice never replays checkout. Its next activation
      // must still verify the official app's save and the current terms.
    });
    node.append(document.createElement('br'), button);
  }

  async function restoreSelection() {
    if (otherTabChanged) { reportOtherTabChange(); return false; }
    if (requiresReselection()) { requestReselection(); return false; }
    const selection = storedSelection();
    if (!selection || !document.querySelector('[data-jp-delivery-checkout]')) return;
    const requestedVersion = cartVersion;
    const requestedRestoration = restorationVersion;
    await choiceSync.prepare();
    if (requestedVersion !== cartVersion || requestedRestoration !== restorationVersion) return false;
    if (checkOtherTab(selection)) return false;
    // With blocked storage/identity, verify the current app without replaying
    // an uncoordinated old preference into the shared cart.
    if (!choiceSync.usable) {
      try {
        await waitForDeliverySave({ readSnapshot, readCart, sleep, expectedRevision: baseline, waitUntilReady: true });
        return true;
      } catch (error) { status(error.message); return false; }
    }
    restoring = true;
    const version = ++restorationVersion;
    const cartAtStart = cartVersion;
    const current = () => {
      if (version !== restorationVersion || cartAtStart !== cartVersion || merchandisePending || refreshing || checkOtherTab(selection)) return false;
      if (!choiceSync.usable) {
        reconciliationPaused = true;
        status('配送指定の共有状態を確認できませんでした。ページを再読み込みするか、配送日時を選び直してください。');
        return false;
      }
      return true;
    };
    status('配送指定を確認しています…');
    try {
      const readCurrentCart = async (timeout) => {
        const cart = await readCart(timeout);
        if (cartRevision(cart) !== baseline) throw new Error('別の画面でカートが変更されました。ページを開き直してください。');
        return cart;
      };
      await readCurrentCart();
      if (!current()) return false;
      const result = await restoreAndVerifyDeliveryChoice({ selection, sleep, isCurrent: current, readSnapshot, readCart: readCurrentCart,
        readControls: () => {
          const app = deliveryApp();
          if (app?.querySelector('#undeliverable-statement')) return { ineligible: true };
          if (!app?.querySelector('#delivery-datetime--isRender')) return null;
          const control = (name) => {
            const node = app.querySelector(`select[name="${name}"]`);
            return node ? { value: node.value, options: [...node.options].filter((option) => !option.disabled).map((option) => option.value) } : null;
          };
          return { ready: true, pending: Boolean(app.querySelector('select:disabled')), prefecture: control('prefecture'), date: control('delivery_date'), time: control('delivery_time') };
        },
        choose: (name, value) => {
          if (!current()) return;
          const node = deliveryApp()?.querySelector(`select[name="${name}"]`);
          if (node) {
            applyingChoice = true;
            try { node.value = value; node.dispatchEvent(new Event('change', { bubbles: true })); }
            finally { applyingChoice = false; }
          }
        },
      });
      if (!current() || result.cancelled) return;
      if (result.ineligible) {
        reconciliationPaused = true;
        await waitForDeliverySave({ readSnapshot, readCart: readCurrentCart, sleep, waitUntilReady: true });
        // Keep the prior preference for removal of the ineligible item; the
        // current checkout still uses the app's saved no-date state.
        if (current()) { status(''); return true; }
        return false;
      }
      if (result.invalid) {
        reconciliationPaused = true;
        reselectionRequired = true;
        rememberSelection({ ...selection, requiresReselection: true });
        requestReselection();
        return false;
      }
      if (current()) { reconciliationPaused = false; rememberSelection(selection); confirmedChoice = choiceCheckpoint(selection); status(''); return true; }
    } catch (error) {
      if (current()) { reconciliationPaused = true; status(error instanceof Error ? error.message : '配送日時を選び直してください。'); }
    } finally { if (version === restorationVersion) restoring = false; }
  }

  async function readCart(timeout = 4000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('カートの更新を確認できませんでした。もう一度お試しください。');
      const cart = await response.json();
      trace('read', { count: cart.item_count });
      // Non-personal readback evidence for independent browser QA. No keys,
      // addresses or cart token are exposed by this adapter.
      const node = document.querySelector('[data-jp-delivery-status]');
      if (node) {
        node.dataset.jpSavedDate = normalizeDeliveryDate(cart.attributes?.['配送希望日']);
        node.dataset.jpSavedTime = cart.attributes?.['配送時間帯'] || '';
        node.dataset.jpSavedItemCount = String(cart.item_count ?? '');
      }
      return cart;
    } catch {
      throw new Error('カートの更新を確認できませんでした。時間をおいてもう一度お試しください。');
    } finally { clearTimeout(timer); }
  }

  function status(message, scope = document) {
    const node = scope.querySelector('[data-jp-delivery-status]') || document.querySelector('[data-jp-delivery-status]');
    if (node) { node.textContent = message; node.hidden = !message; }
  }

  function lockMerchandise(locked) {
    for (const node of document.querySelectorAll('cart-items-component')) node.inert = locked;
  }

  function lockDelivery(locked) {
    for (const node of document.querySelectorAll(appSelector)) node.inert = locked;
  }

  function validTerms(form) {
    const terms = document.getElementById(form.id === 'cart-form' ? 'jp-cart-drawer-terms' : 'jp-cart-terms');
    if (cartTermsSatisfied(form, terms)) return true;
    if (terms?.required && terms.form === form) {
      terms.focus();
      terms.reportValidity();
    } else {
      status('利用規約の確認欄を読み込めませんでした。ページを再読み込みして、もう一度お試しください。');
    }
    return false;
  }

  async function verifyCheckout(button) {
    if (verifying || refreshing || restoring) return;
    verifying = true;
    const version = cartVersion;
    button.setAttribute('aria-busy', 'true');
    status('配送指定の保存を確認しています…');
    try {
      await choiceSync.prepare();
      if (checkOtherTab()) return;
      await checkSavedChoice(true);
      if (requiresReselection()) { requestReselection(); return; }
      if (storedSelection()) {
        if (!await restoreSelection()) return;
      } else {
        await waitForDeliverySave({ readSnapshot, readCart, sleep, expectedRevision: button.dataset.jpCartRevision });
      }
      if (!button.isConnected || button.disabled || version !== cartVersion || deliveryStale || merchandisePending || refreshing || restoring || document.querySelector('.cart-items-disabled') || !validTerms(button.form)) {
        if (!merchandisePending && !refreshing) status('カートまたは配送指定が変わりました。確認してもう一度お試しください。');
        return;
      }
      if (checkOtherTab()) return;
      approvedButton = button;
      status('');
      button.click(); // Replay the app's own checkout/address handler and native validation.
    } catch (error) {
      status(error instanceof Error ? error.message : '配送指定を確認できませんでした。カートページで確認してください。');
    } finally {
      approvedButton = null;
      verifying = false;
      button.removeAttribute('aria-busy');
    }
  }

  function guard(event) {
    const button = event.type === 'click'
      ? event.target?.closest?.('[data-jp-delivery-checkout]')
      : event.submitter || event.target?.querySelector?.('[name="checkout"]') || document.querySelector(`[data-jp-delivery-checkout][form="${event.target?.id}"]`);
    if (!button?.matches?.('[data-jp-delivery-checkout]')) return;
    if (!button.form || button.form.id !== button.getAttribute('form')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      status('カートの確認欄を読み込めませんでした。ページを再読み込みして、もう一度お試しください。');
      return;
    }
    if (event.type === 'submit' && event.target !== button.form) return;
    if (validTerms(button.form) && approvedButton === button && !merchandisePending && !deliveryStale && !refreshing && !restoring && !document.querySelector('.cart-items-disabled')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!validTerms(button.form)) return;
    if (checkOtherTab()) { reportOtherTabChange(); return; }
    if (restoring) { status('配送指定を確認しています。完了後にもう一度お試しください。'); return; }
    if (merchandisePending || refreshing || deliveryStale || document.querySelector('.cart-items-disabled')) {
      status(deliveryStale && !refreshing ? '配送条件を更新できませんでした。カートページで確認してください。' : 'カートを更新しています。完了後にもう一度お試しください。');
      return;
    }
    void verifyCheckout(button);
  }

  window.addEventListener('click', guard, { capture: true });
  window.addEventListener('submit', guard, { capture: true });
  document.addEventListener('change', (event) => {
    if (!event.target?.closest?.(appSelector)) return;
    deliveryEdited = true;
    if (applyingChoice) return;
    if (requiresReselection()) {
      const control = event.target;
      // App hydration and programmatic change events cannot acknowledge the
      // warning. Only a real choice in an enabled official date/time select can.
      if (!event.isTrusted || control.tagName !== 'SELECT' || !['delivery_date', 'delivery_time'].includes(control.name)
        || control.disabled || control.selectedOptions?.[0]?.disabled || !readSelection()) {
        requestReselection();
        return;
      }
      reselectionRequired = false;
      status('');
    }
    if (otherTabChanged && !event.isTrusted) { reportOtherTabChange(); return; }
    if (event.isTrusted) otherTabChanged = false;
    reconciliationPaused = false;
    if (restoring) { restorationVersion++; restoring = false; status(''); }
    cartVersion++;
    rememberReadableChoice(event.target.name === 'prefecture' ? event.target.value : undefined);
    if (event.isTrusted) publishExplicitChoice();
  }, { capture: true });
  document.addEventListener('jp:cart-quantity-queued', (event) => {
    if (event.detail?.queued) quantityOwners.add(event.detail.owner);
    else quantityOwners.delete(event.detail?.owner);
    quantityQueued = quantityOwners.size > 0;
    merchandisePending = quantityQueued || activeUpdates > 0;
    cartVersion++;
    if (event.detail?.queued) rememberCurrentChoice();
    trace('quantity-reservation', { queued: quantityQueued });
  });
  document.addEventListener('jp:cart-mutation-started', (event) => {
    rememberCurrentChoice();
    activeUpdates++;
    if (event.detail?.kind === 'quantity') {
      quantityOwners.delete(event.detail.owner);
      quantityQueued = quantityOwners.size > 0;
    }
    cartVersion++;
    merchandisePending = true;
    trace('started', { type: event.detail?.kind });
    lockDelivery(true);
    lockMerchandise(true);
    const queued = queueMerchandiseMutation({ kind: event.detail?.kind, queue: mutationQueue,
      isQuantityQueued: () => quantityOwners.size > 0, sleep, prepare: async () => {
      // All theme merchandise requests wait for the current AMP attribute
      // queue to finish. Delivery inputs stay inert while merchandise changes.
      await choiceSync.prepare();
      if (checkOtherTab()) throw new Error('別のタブで配送指定が変更されました。ページを再読み込みして確認してください。');
      if (deliveryEdited || readSnapshot()) {
        status('配送指定を保存しています…');
        try { await prepareDeliveryMutation({ edited: deliveryEdited, readSnapshot, readCart, sleep }); }
        catch (error) { status(error instanceof Error ? error.message : '配送指定を保存できませんでした。'); throw error; }
      }
      trace('mutation-ready');
    } });
    releases.set(event.detail, queued.settle);
    event.detail?.waitUntil?.(queued.ready);
  });
  document.addEventListener('jp:cart-mutation-settled', (event) => {
    releases.get(event.detail)?.();
    releases.delete(event.detail);
    activeUpdates = Math.max(0, activeUpdates - 1);
    merchandisePending = quantityQueued || activeUpdates > 0;
    trace('settled');
    if (!merchandisePending && !refreshing) lockDelivery(false);
    if (!merchandisePending && !refreshing) lockMerchandise(false);
    // A merchandise update cancels any older restoration via cartVersion;
    // its pending read must not leave this update's progress text behind.
    if (!merchandisePending && !refreshing
      && document.querySelector('[data-jp-delivery-status]')?.textContent === '配送指定を保存しています…') status('');
  });

  // Rebuild AMP from the new merchandise, then replay the recorded choices
  // through its current options and verify its save before checkout. Requiring
  // the old date to stay saved here would deadlock a legitimate app reset.
  document.addEventListener('cart:update', async (event) => {
    cartVersion++;
    trace('cart-event', { source: event.detail?.data?.source, count: event.detail?.resource?.item_count });
    const drawer = document.querySelector('cart-drawer-component [data-jp-cart-revision]');
    const page = event.target?.closest?.('cart-items-component:not([data-drawer])');
    if ((!drawer && !page) || refreshing) return;
    // A delivery-attribute event is not a merchandise change. The page's
    // full morph is owned by the quantity component, matching the old contract.
    if (page && event.detail?.data?.source !== 'cart-items-component') return;
    deliveryStale = true;
    refreshing = true;
    status('配送条件を更新しています…');
    try {
      const cart = await readSettledCart({ readCart, sleep, isPending: () => merchandisePending, getVersion: () => cartVersion });
      if (!page && cartRevision(cart) === baseline) { deliveryStale = false; status(''); return; }
      if (!cart.items?.length) rememberSelection(null);
      else {
        const selection = storedSelection(true);
        if (selection) rememberSelection(selection, cartRevision(cart));
      }
      const drawerComponent = drawer?.closest('cart-drawer-component');
      // The native CartAdd handler already honours auto-open. Observe the
      // resulting open state here, including a user closing it while waiting.
      if (drawerComponent?.querySelector('dialog')?.open) {
        sessionStorage.setItem('jp-cart-reopen', JSON.stringify({ path: location.pathname + location.search, at: Date.now() }));
      }
      reloadQueued = true;
      trace('reload', { count: cart.item_count });
      location.reload();
    } catch (error) {
      status(error instanceof Error ? error.message : '配送条件を更新できませんでした。カートページで確認してください。');
    } finally {
      if (!reloadQueued) {
        refreshing = false;
        if (!merchandisePending) lockDelivery(false);
        if (!merchandisePending) lockMerchandise(false);
      }
    }
  });

  async function reopenDrawer() {
    let saved;
    try {
      saved = JSON.parse(sessionStorage.getItem('jp-cart-reopen') || 'null');
      sessionStorage.removeItem('jp-cart-reopen');
    } catch { return; }
    if (!saved || saved.path !== location.pathname + location.search || Date.now() - saved.at > 60000) return;
    await customElements.whenDefined('cart-drawer-component');
    document.querySelector('cart-drawer-component')?.open();
  }
  const start = () => {
    if (document.querySelector('[data-jp-delivery-checkout]')) void choiceSync.prepare();
    void restoreSelection().catch((error) => status(error.message));
    void reopenDrawer();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  // Official app hydration can also finish after the first saved confirmation.
  // Also check saved attributes at most once per two seconds while a visible
  // confirmed choice is idle. Quantity/AMP hydration never run this monitor.
  setInterval(() => {
    if (document.hidden || restoring || verifying || refreshing || merchandisePending || reconciliationPaused) return;
    const app = deliveryApp();
    if (!app?.getClientRects().length) return;
    const intended = storedSelection();
    const actual = readSelection();
    if (intended && actual && (intended.prefecture !== actual.prefecture || intended.date !== actual.date || intended.time !== actual.time)) void restoreSelection();
    else void checkSavedChoice();
  }, 500);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) void checkSavedChoice(true); });
  window.addEventListener('focus', () => { void checkSavedChoice(true); });
}
